// ViewBoards.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './Dashboard.css'; // Reuse dashboard styles
import ProfilePicture from './ProfilePicture';
import AuthModal from './AuthModal';
import { Target, Map, LogOut, User, Menu, ChevronLeft, Download, Check, Trash2 } from 'lucide-react';
import DownloadDialog from './DownloadDialog';
import { deliverFiles } from './lib/deliverFiles';

// Memoized grid item — skips re-render unless its own state changes
const GridItem = React.memo(({ image, isActive, isSelected, isSelectMode, onHover, onClick, onDoubleClick, onToggleSelect }) => (
  <div
    className={`grid-item ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
    onMouseEnter={() => onHover(image)}
    onClick={(e) => {
      if (isSelectMode) {
        e.preventDefault();
        e.stopPropagation();
        onToggleSelect(image.id);
      } else {
        onClick(image, e);
      }
    }}
    onDoubleClick={(e) => onDoubleClick(image, e)}
  >
    <img
      src={getThumbUrl(image)}
      alt=""
      onError={(e) => {
        const img = e.target;
        const src = img.src;
        if (src.includes('__ia_thumb.jpg')) {
          img.src = image.thumbnailUrl;
        } else if (!src.includes('/api/proxy-image')) {
          img.src = `/api/proxy-image?url=${encodeURIComponent(image.thumbnailUrl)}`;
        } else {
          img.onerror = null;
          img.src = "https://archive.org/images/notfound.png";
        }
      }}
    />
    {isSelectMode && isSelected && (
      <div className="select-check">
        <Check size={16} />
      </div>
    )}
  </div>
), (prev, next) => {
  // Only re-render if this specific item's visual state changed
  return prev.image.id === next.image.id &&
    prev.isActive === next.isActive &&
    prev.isSelected === next.isSelected &&
    prev.isSelectMode === next.isSelectMode;
});

// Convert Archive.org redirect URL to direct URL (avoids 302 round-trip)
function getThumbUrl(image) {
  const url = image.thumbnailUrl;
  if (url && url.includes('archive.org/services/img/')) {
    const id = url.split('/services/img/')[1];
    return `https://archive.org/download/${id}/__ia_thumb.jpg`;
  }
  return url;
}

function ViewBoards() {
  const { username } = useParams();
  const [savedImages, setSavedImages] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [viewedUser, setViewedUser] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [downloadRequest, setDownloadRequest] = useState(null);
  const [isPreviewLocked, setIsPreviewLocked] = useState(false);
  const [activeBoard, setActiveBoard] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '' });
  const [showModal, setShowModal] = useState(false);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState(new Set());


  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setIsAuthenticated(true);
        setCurrentUser(userData);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        handleLogout();
      }
    } else {
      setLoading(false);
    }
  }, []);

  // No redirect when viewing own boards — /:username is the shareable link

  // Fetch user information (works with or without auth)
  useEffect(() => {
    if (username) {
      fetchUserInfo();
    }
  }, [username]);

  // Fetch boards when user info is available
  useEffect(() => {
    if (viewedUser) {
      fetchBoards();
    }
  }, [viewedUser]);

  // Fetch images when activeBoard changes
  useEffect(() => {
    if (viewedUser && activeBoard) {
      fetchSavedImages(activeBoard);
    }
  }, [viewedUser, activeBoard]);

  // Click outside: unlock and clear preview
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.grid-item') &&
          !event.target.closest('.preview-container')) {
        setIsPreviewLocked(false);
        setPreviewImage(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      // Use public endpoint - works without auth
      const response = await fetch(`/api/shared-boards/public/${username}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('User not found');
        }
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      setViewedUser(data.user);
      setError(null);
    } catch (err) {
      console.error('Fetch user error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedImages = async (boardId) => {
    try {
      setLoading(true);
      // Use public endpoint by username
      const url = boardId === 'all'
        ? `/api/shared-images/public/${username}`
        : `/api/shared-images/public/${username}?boardId=${boardId}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      setSavedImages(data);
      setError(null);
    } catch (err) {
      console.error('Fetch images error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoards = async () => {
    try {
      // Use public endpoint
      const response = await fetch(`/api/shared-boards/public/${username}`);

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      let responseData = await response.json();
      let data = responseData.boards || responseData;

      // Create virtual "All" board and put it first
      const allBoard = {
        id: 'all',
        name: 'All',
        _count: {
          savedImages: data.reduce((total, board) => total + board._count.savedImages, 0)
        },
        isVirtual: true
      };

      // Add the "All" board at the beginning
      data = [allBoard, ...data];
      setBoards(data);

      // Set active board to "All" if none is selected
      if (!activeBoard) {
        setActiveBoard('all');
      }
    } catch (err) {
      console.error('Fetch boards error:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setCurrentUser(null);
    window.location.href = '/';
  };

  // Hover: update preview
  const handleImageHover = (image) => {
    if (!isPreviewLocked) {
      setPreviewImage(image);
    }
  };

  // Single click: lock preview to this image (or switch lock to new image)
  // Cmd+click: select/deselect for multi-download
  const handleImageClick = (image, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Cmd+click (Mac) or Ctrl+click (Windows) to select/deselect
    if (event.metaKey || event.ctrlKey) {
      // If entering select mode and there's a locked preview, include it too
      if (!isSelectMode && isPreviewLocked && previewImage) {
        setSelectedImages(prev => {
          const next = new Set(prev);
          next.add(previewImage.id);
          next.add(image.id);
          return next;
        });
      } else {
        toggleImageSelection(image.id);
      }
      if (!isSelectMode) setIsSelectMode(true);
      return;
    }

    // Single click: lock preview to this image
    setPreviewImage(image);
    setIsPreviewLocked(true);
  };

  // Double-click: toggle selection
  const handleImageDoubleClick = (image, event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleImageSelection(image.id);
    if (!isSelectMode) setIsSelectMode(true);
  };

  const handleBoardClick = (boardId) => {
    setActiveBoard(boardId);
    setPreviewImage(null);
    setIsPreviewLocked(false);
  };

  const toggleImageSelection = (imageId) => {
    setSelectedImages(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  };

  // Archive.org answers 200 with its own logo for items that have no image, so
  // a notfound URL is a known-dead entry rather than something worth fetching.
  const isDownloadable = (image) => {
    const url = image.fullSizeUrl || image.thumbnailUrl;
    return !!url && url !== 'https://archive.org/images/notfound.png';
  };

  // Every download here goes through the shared dialog, so a public profile
  // offers the same quality tiers and byte progress as the owner's dashboard.
  const requestDownload = (images, zipName) => {
    const downloadable = images.filter(isDownloadable);
    if (downloadable.length === 0) {
      showNotification('No images to download');
      return;
    }
    setDownloadRequest({ images: downloadable, zipName });
  };

  const downloadAsZip = (images) => requestDownload(images, `${username}-images.zip`);

  const handleDownloadSelected = () =>
    requestDownload(savedImages.filter((img) => selectedImages.has(img.id)), `${username}-images.zip`);

  const handleDownloadSingle = (image) => requestDownload([image], 'image.zip');

  const runDownload = async (files, setPercent, { failed = 0 } = {}) => {
    const outcome = await deliverFiles(files, {
      zipName: downloadRequest.zipName,
      onZipPercent: setPercent,
    });
    if (outcome === 'cancelled') return;
    const saved = `Saved ${files.length} image${files.length === 1 ? '' : 's'}`;
    showNotification(failed > 0 ? `${saved} (${failed} failed)` : saved);
  };

  // Save image to user's collection
  const handleSaveImage = async (image) => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/saved-images', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          archiveId: image.archiveId,
          title: image.title,
          description: image.description,
          url: image.url,
          thumbnailUrl: image.thumbnailUrl,
          fullSizeUrl: image.fullSizeUrl || null
        })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      showNotification('Image saved to your collection');
    } catch (err) {
      console.error('Save image error:', err);
      showNotification('Error saving image: ' + err.message);
    }
  };

  const showNotification = (message) => {
    setNotification({ show: true, message });
    setTimeout(() => {
      setNotification({ show: false, message: '' });
    }, 2000);
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const toggleSidebar = () => {
    setIsSidebarMinimized(!isSidebarMinimized);
  };

  if (!isAuthenticated && !loading) {
    return (
      <div className="page-container">
        {/* Sidebar with minimize functionality */}
        <div className={`sidebar ${isSidebarMinimized ? 'minimized' : ''}`}>
          {!isSidebarMinimized ? (
            <div className="sidebar-nav">
              <button className="nav-button minimize-button" onClick={toggleSidebar} title="Minimize sidebar">
                <ChevronLeft size={18} />
              </button>
              <button className="nav-button" onClick={toggleModal} title="Login"><User size={18} /></button>
            </div>
          ) : (
            <button className="nav-button expand-button" onClick={toggleSidebar} title="Expand sidebar">
              <Menu size={18} />
            </button>
          )}
        </div>

        <div className="dashboard-container">
          <div className="not-authenticated">
            <h2>Please log in to view collections</h2>
            <button className="return-button" onClick={() => window.location.href = '/'}>
              Return to Home
            </button>
          </div>
        </div>

        {/* Auth Modal */}
        <AuthModal
          isOpen={showModal}
          onClose={(success, userData) => {
            setShowModal(false);
            if (success && userData) {
              setIsAuthenticated(true);
              setCurrentUser(userData);
            }
          }}
        />
      </div>
    );
  }

  if (loading && !viewedUser) {
    return <div className="dashboard-loading">Loading collection...</div>;
  }

  if (error) {
    return <div className="dashboard-error">Error: {error}</div>;
  }

  // Only show active highlight when locked
  const activeImageId = isPreviewLocked ? previewImage?.id : null;

  return (
    <div className="page-container">
      {notification.show && (
        <div className="notification">
          {notification.message}
        </div>
      )}

      {/* Sidebar with minimize functionality */}
      <div className={`sidebar ${isSidebarMinimized ? 'minimized' : ''}`}>
        {!isSidebarMinimized ? (
          <div className="sidebar-nav">
            <button className="nav-button minimize-button" onClick={toggleSidebar} title="Minimize sidebar">
              <ChevronLeft size={18} />
            </button>
            {isAuthenticated ? (
              <>
                <a href="/" className="nav-button" title="Home"><Target size={18} /></a>
                <a href={currentUser ? `/${currentUser.username}` : '/dashboard'} className="nav-button" title="Profile"><User size={18} /></a>
                <a href="/map" className="nav-button" title="Map"><Map size={18} /></a>
                <button className="nav-button" onClick={handleLogout} title="Logout"><LogOut size={18} /></button>
              </>
            ) : (
              <button className="nav-button" onClick={toggleModal} title="Login"><User size={18} /></button>
            )}
          </div>
        ) : (
          <button className="nav-button expand-button" onClick={toggleSidebar} title="Expand sidebar">
            <Menu size={18} />
          </button>
        )}
      </div>

      {/* Profile Header - only show when viewing someone else's profile */}
      {!(currentUser && currentUser.username === username) && (
        <div className="profile-header-top">
          {viewedUser && <ProfilePicture user={viewedUser} editable={false} />}
          <h1>{viewedUser?.username || 'User'}'s Collection</h1>
        </div>
      )}

      {/* Dashboard Container */}
      <div className="dashboard-container">

        <div className="dashboard-content">
        {/* Left Container - Preview */}
        <div className="preview-container">
          {previewImage ? (
            <div className="preview-content">
              <div className="preview-image-wrapper">
                <img
                  src={getThumbUrl(previewImage)}
                  alt=""
                  onError={(e) => {
                    const img = e.target;
                    const src = img.src;
                    if (src.includes('__ia_thumb.jpg')) {
                      img.src = previewImage.thumbnailUrl;
                    } else if (!src.includes('/api/proxy-image')) {
                      img.src = `/api/proxy-image?url=${encodeURIComponent(previewImage.thumbnailUrl)}`;
                    } else {
                      img.onerror = null;
                      img.src = "https://archive.org/images/notfound.png";
                    }
                  }}
                />
              </div>
              <div className="preview-metadata">
                <h3>{previewImage.title}</h3>
                {previewImage.description && <p>{previewImage.description}</p>}
                <div className="preview-actions">
                  <a
                    href={previewImage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-button"
                  >
                    View source
                  </a>
                  {currentUser && currentUser.username === username ? (
                    <>
                      <button
                        className="save-button"
                        onClick={() => handleDownloadSingle(previewImage)}
                      >
                        <Download size={14} /> Download
                      </button>
                      <button
                        className="save-button"
                        style={{ backgroundColor: '#cc3333', marginTop: '4px' }}
                        onClick={async () => {
                          if (!window.confirm('Delete this image?')) return;
                          const token = localStorage.getItem('token');
                          try {
                            const res = await fetch(`/api/saved-images/${previewImage.id}`, {
                              method: 'DELETE',
                              headers: { 'x-auth-token': token }
                            });
                            if (res.ok) {
                              showNotification('Image deleted');
                              setPreviewImage(null);
                              setIsPreviewLocked(false);
                              fetchSavedImages(activeBoard);
                            }
                          } catch (err) {
                            console.error('Delete error:', err);
                          }
                        }}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </>
                  ) : (
                    <button
                      className="save-button"
                      onClick={() => handleSaveImage(previewImage)}
                    >
                      Save to my collection
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="preview-placeholder">
              <p>Hover over an image to preview<br />Click to lock preview</p>
            </div>
          )}
        </div>

        {/* Middle Container - Image Grid */}
        <div className="image-grid-container">
          {loading ? (
            <div className="loading-indicator">Loading images...</div>
          ) : savedImages.length === 0 ? (
            <div className="no-images-message">
              <p>No images in this board</p>
            </div>
          ) : (
            <div className="image-grid">
              {savedImages.map((image) => (
                <GridItem
                  key={image.id}
                  image={image}
                  isActive={activeImageId === image.id}
                  isSelected={selectedImages.has(image.id)}
                  isSelectMode={isSelectMode}
                  onHover={handleImageHover}
                  onClick={handleImageClick}
                  onDoubleClick={handleImageDoubleClick}
                  onToggleSelect={toggleImageSelection}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right Container - Boards */}
        <div className="boards-container">
          <div className="boards-header">
            <h2>Boards</h2>
          </div>

          <div className="board-list">
            {boards.map(board => (
              <div
                key={board.id}
                className={`board-item ${activeBoard === board.id ? 'active' : ''} ${board.id === 'all' ? 'all-board' : ''}`}
                onClick={() => handleBoardClick(board.id)}
              >
                <span>{board.name}</span>
                <span className="image-count">
                  {board._count.savedImages} {board._count.savedImages === 1 ? 'image' : 'images'}
                </span>
              </div>
            ))}
          </div>
          {currentUser && currentUser.username === username && savedImages.length > 0 && (
            <>
              <button
                className="download-all-button"
                onClick={() => downloadAsZip(savedImages)}
              >
                <Download size={14} /> Download All
              </button>
            </>
          )}
        </div>
        </div>
      </div>

      {/* Desktop selection bar */}
      {isSelectMode && selectedImages.size > 0 && (
        <div className="desktop-select-bar">
          <span className="select-count">{selectedImages.size} selected</span>
          <button className="mobile-save-btn" onClick={handleDownloadSelected}>
            Download Selected
          </button>
          <button
            className="mobile-save-btn cancel"
            onClick={() => { setIsSelectMode(false); setSelectedImages(new Set()); }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showModal}
        onClose={(success, userData) => {
          setShowModal(false);
          if (success && userData) {
            setIsAuthenticated(true);
            setCurrentUser(userData);
          }
        }}
      />

      {downloadRequest && (
        <DownloadDialog
          images={downloadRequest.images}
          onClose={() => setDownloadRequest(null)}
          onReady={runDownload}
        />
      )}
    </div>
  );
}

export default ViewBoards;
